import { Fragment, useEffect, useRef, useState } from 'react';
/**
 * The API reference — the SPA's rendering of views/api-reference.ejs.
 *
 * Everything here is the EJS document: the same cards in the same order, the same ids (the EJS page's jump
 * links `#api-mcp` / `#api-rest` / `#api-session-only` are anchors into it), the same copy buttons on every
 * code block, and its prose — the reference's value is the prose, which the SPA had replaced with a summary.
 * The source of both is `services/api-docs.js` plus `mcpSetup`, so the two pages cannot promise different
 * endpoints; only the rendering differs.
 */
import { Table } from '../components/Table';

export type McpSetup = {
  base: string;
  endpoint: string;
  whoami: string;
  activate: string;
  pairCodeMinutes: number;
  rateLimit: number;
  tools: { name: string; scope: string; title?: string; summary?: string; description?: string }[];
};

type McpDoc = {
  method: string;
  path: string;
  summary: string;
  transport: string;
  handshake: string[];
  scope: string;
  headers: { name: string; value: string; note?: string }[];
  examples: { title: string; request: string; response: string; note?: string }[];
  errors?: string[];
  notes?: string[];
};

type DeviceDoc = {
  summary: string;
  facts: string[];
  flow: { step: string; request: string; response: string; note?: string }[];
  poll: { note: string; body: string; extra?: string };
  codes: { code: string; meaning: string }[];
  endpoints: { method: string; path: string; auth: string; summary: string }[];
};

type RestDoc = {
  method: string;
  path: string;
  scope: string;
  summary: string;
  params: { name: string; description: string; required?: boolean }[];
  shapes?: { when: string; shape: string; mode?: string }[];
  fields?: string[];
  rangeFields?: string[];
  example: string;
  returns: string;
  errors?: string[];
  notes?: string[];
};

export type ApiDocs = {
  MCP: McpDoc;
  REST: RestDoc[];
  SESSION_ONLY: { path: string; note: string }[];
  DEVICE: DeviceDoc;
};

/** Backticks in the docs module mark code spans (views/api-reference.ejs's `rich`) — and nothing else is
 *  wrapped: React escapes the plain segments itself, so they stay bare text nodes rather than spans the EJS
 *  page doesn't have. */
function rich(x: string | null | undefined) {
  return String(x == null ? '' : x)
    .split('`')
    .map((seg, i) =>
      i % 2 === 1 ? (
        <span key={i} className="api-mono">
          {seg}
        </span>
      ) : (
        seg
      ),
    );
}

/** The docs module keeps "<base>" so it stays portable; the real, request-derived URL is known here. */
const withBase = (s: string, base: string) => String(s == null ? '' : s).replace(/<base>/g, base);

function Copy({ text }: { id: string; text: string }) {
  // A raw ghost button, as views/api-reference.ejs writes it: `class="ghost js-copy"`. Going through the Button
  // component adds `btn`, and `.btn` is what owns height and padding — a taller, wider Copy than the EJS page's.
  //
  // The EJS page's control goes through the layout's delegated .js-copy handler, which flashes "Copied" for 1.4s
  // (and "Press Ctrl+C" when the clipboard is refused). The SPA copied silently, so a click looked like nothing
  // had happened — same label, no confirmation that anything reached the clipboard.
  const [label, setLabel] = useState('Copy');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return (
    <button
      type="button"
      className="ghost js-copy"
      onClick={async () => {
        let said = 'Copied';
        try {
          if (!navigator.clipboard) throw new Error('no clipboard');
          await navigator.clipboard.writeText(text);
        } catch {
          said = 'Press Ctrl+C';
        }
        setLabel(said);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setLabel('Copy'), 1400);
      }}
    >
      {label}
    </button>
  );
}

function Code({ id, text, copyId, gap = 8 }: { id: string; text: string; copyId: string; gap?: number }) {
  return (
    <div className="copy-row" style={{ marginBottom: gap }}>
      <pre className="api-example" id={id}>
        {text}
      </pre>
      <Copy id={copyId} text={text} />
    </div>
  );
}

export function ApiReference({ setup, docs }: { setup?: McpSetup; docs?: ApiDocs }) {
  if (!setup || !docs) {
    return (
      <div id="tab-api" role="tabpanel">
        <section className="panel-card" style={{ marginTop: 0 }}>
          <p style={{ color: 'var(--muted)', margin: 0 }}>
            The reference needs the app&apos;s documentation module, which this render did not receive.
          </p>
        </section>
      </div>
    );
  }
  const readTools = setup.tools.filter((t) => t.scope !== 'read-write');
  const writeTools = setup.tools.filter((t) => t.scope === 'read-write');
  const mcp = docs.MCP;
  const dev = docs.DEVICE;

  return (
    <>
      <div className="panel-card" style={{ marginTop: 0 }}>
        <h2 style={{ margin: '0 0 6px' }}>Everything a token can call</h2>
        <p className="api-lede">
          Nothing here needs a browser session, and none of it needs your password: every request below carries
          the token as a bearer credential. The two pairing endpoints are the exception only in that the browser
          which approves a device has to be signed in — the device itself never is.
        </p>
        <div style={{ display: 'grid', gap: 6 }}>
          <label htmlFor="api-base">Base URL (this instance)</label>
          <div className="copy-row">
            <input id="api-base" readOnly value={setup.base} className="api-input" onClick={(e) => e.currentTarget.select()} />
            <Copy id="api-base-copy" text={setup.base} />
          </div>
          <label htmlFor="api-auth">Every request carries</label>
          <div className="copy-row">
            <input
              id="api-auth"
              readOnly
              value="Authorization: Bearer <your-token>"
              className="api-input"
              onClick={(e) => e.currentTarget.select()}
            />
            <Copy id="api-auth-copy" text="Authorization: Bearer <your-token>" />
          </div>
        </div>
        <p className="api-jump">
          Rate limited to <strong>{setup.rateLimit}</strong> calls a minute per token. Jump to:{' '}
          <a href="#api-mcp">MCP</a> · <a href="#api-pairing">Pairing a device</a> ·{' '}
          <a href="#api-rest">REST</a> (<a href="#api-whoami">whoami</a>, <a href="#api-prices">prices</a>,{' '}
          <a href="#api-session-only">session-only routes</a>).
        </p>
      </div>

      <div className="panel-card" id="api-mcp">
        <h2 style={{ margin: '0 0 6px' }}>
          MCP{' '}
          <span className="api-mono" style={{ fontWeight: 400, color: 'var(--muted)' }}>
            {mcp.method} {mcp.path}
          </span>
        </h2>
        <p className="api-note">
          {mcp.summary} Transport: {mcp.transport}. A client performs the standard handshake —{' '}
          {/* The page writes each handshake step as a bare `api-mono` span in this paragraph, arrow between
              them; a wrapper span around each step is one extra empty span (seven on this page). */}
          {mcp.handshake.map((h, i) => (
            <Fragment key={h}>
              <span className="api-mono" style={{ color: 'var(--text)' }}>
                {h}
              </span>
              {i < mcp.handshake.length - 1 ? ' → ' : ''}
            </Fragment>
          ))}{' '}
          — and the tools it may call depend on the token&apos;s scope ({mcp.scope}).
        </p>
        <Table
          expandable={false}
          wrap
          tableClass="api-table"
          style={{ margin: '0 0 14px' }}
          columns={[{ label: 'Header', width: 150 }, { label: 'Value' }]}
          rows={mcp.headers.map((h, i) => ({
            key: `${h.name}-${i}`,
            cells: [
              <span key="n" className="api-mono">
                {h.name}
              </span>,
              // The page's cell is the code span, then the note span — no wrapper, and the space before the
              // dash belongs to the text, not to the note span.
              <Fragment key="v">
                <span className="api-mono">{h.value}</span>
                {h.note ? (
                  <Fragment>
                    {' '}
                    <span style={{ color: 'var(--muted)' }}>— {rich(h.note)}</span>
                  </Fragment>
                ) : null}
              </Fragment>,
            ],
          }))}
        />

        <h3 className="api-h3">What a call looks like</h3>
        {mcp.examples.map((ex, i) => (
          <Fragment key={ex.title}>
            <p className="api-route-head">
              <strong>{ex.title}</strong>
            </p>
            <p className="api-note" style={{ marginBottom: 6 }}>
              Request
            </p>
            <Code id={`mcp-ex-${i}-req`} copyId={`mcp-ex-${i}-req-copy`} text={ex.request} />
            <p className="api-note" style={{ marginBottom: 6 }}>
              Response
            </p>
            <Code id={`mcp-ex-${i}-res`} copyId={`mcp-ex-${i}-res-copy`} text={ex.response} />
            <p className="api-note" style={{ marginBottom: 18 }}>
              {rich(ex.note)}
            </p>
          </Fragment>
        ))}

        <h3 className="api-h3">What can go wrong</h3>
        <ul className="api-list">
          {(mcp.errors || []).map((x, i) => (
            <li key={i}>{rich(x)}</li>
          ))}
        </ul>

        <h3 className="api-h3">Notes</h3>
        <ul className="api-list" style={{ marginBottom: 18 }}>
          {(mcp.notes || []).map((n, i) => (
            <li key={i}>{rich(n)}</li>
          ))}
        </ul>

        <h3 className="api-h3">Tools</h3>
        <p className="api-note" style={{ marginBottom: 6 }}>
          <strong>Any token can call these ({readTools.length})</strong>
        </p>
        <ul className="api-list" style={{ marginBottom: 16 }}>
          {readTools.map((t) => (
            <li key={t.name}>
              <span className="api-mono" style={{ color: 'var(--text)' }}>
                {t.name}
              </span>{' '}
              — {t.title}
              <br />
              <span style={{ color: 'var(--muted)' }}>{t.description || t.summary || ''}</span>
            </li>
          ))}
        </ul>
        <p className="api-note" style={{ marginBottom: 6 }}>
          <strong>A read-write token adds these ({writeTools.length})</strong>
        </p>
        <ul className="api-list" style={{ marginBottom: 0 }}>
          {writeTools.map((t) => (
            <li key={t.name}>
              <span className="api-mono" style={{ color: 'var(--text)' }}>
                {t.name}
              </span>{' '}
              — {t.title}
              <br />
              <span style={{ color: 'var(--muted)' }}>{t.description || t.summary || ''}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="panel-card" id="api-pairing">
        <h2 style={{ margin: '0 0 6px' }}>Pairing a device that cannot type</h2>
        <p className="api-note">{rich(dev.summary)}</p>
        <ul className="api-list" style={{ margin: '0 0 16px', color: 'var(--muted)' }}>
          {dev.facts.map((f, i) => (
            <li key={i} style={{ marginBottom: 3 }}>
              {rich(f)}
            </li>
          ))}
        </ul>

        <h3 className="api-h3">The three steps</h3>
        {dev.flow.map((f, i) => (
          <Fragment key={f.step}>
            <p className="api-route-head">
              <strong>
                {i + 1}. {f.step}
              </strong>
            </p>
            <p className="api-note" style={{ marginBottom: 6 }}>
              Request
            </p>
            <Code id={`pair-${i}-req`} copyId={`pair-${i}-req-copy`} text={f.request} />
            <p className="api-note" style={{ marginBottom: 6 }}>
              Response
            </p>
            <Code id={`pair-${i}-res`} copyId={`pair-${i}-res-copy`} text={f.response} />
            <p className="api-note" style={{ marginBottom: 18 }}>
              {rich(f.note)}
            </p>
          </Fragment>
        ))}

        <h3 className="api-h3">Polling, and what a failure looks like</h3>
        <p className="api-note">{rich(dev.poll.note)}</p>
        <Code id="pair-poll-body" copyId="pair-poll-body-copy" text={dev.poll.body} />
        <p className="api-note">{rich(dev.poll.extra)}</p>

        <Table
          expandable={false}
          wrap
          tableClass="api-table"
          style={{ margin: '0 0 12px' }}
          columns={[{ label: 'code', width: 200 }, { label: 'What it means' }]}
          rows={dev.codes.map((c) => ({
            key: c.code,
            cells: [
              <span key="c" className="api-mono">
                {c.code}
              </span>,
              <span key="m" style={{ color: 'var(--muted)' }}>
                {c.meaning}
              </span>,
            ],
          }))}
        />

        <h3 className="api-h3">Endpoints</h3>
        {dev.endpoints.map((e) => (
          <div className="api-endpoint" key={e.path}>
            <p>
              <span className="api-mono" style={{ color: 'var(--text)' }}>
                {e.method} {e.path}
              </span>
              <span className="tag" style={{ marginLeft: 6 }}>
                {e.auth === 'none' ? 'no credential' : 'browser session'}
              </span>
            </p>
            <p className="api-endpoint-what">{rich(e.summary)}</p>
          </div>
        ))}
      </div>

      <div className="panel-card" id="api-rest">
        <h2 style={{ margin: '0 0 10px' }}>REST</h2>
        {docs.REST.map((e) => {
          const slug = e.path.replace(/[^a-z]+/gi, '-');
          return (
            <div
              className="api-route"
              key={e.path}
              id={`api-${e.path === '/api/whoami' ? 'whoami' : e.path === '/api/prices' ? 'prices' : slug}`}
            >
              <p className="api-route-head">
                <span
                  className="api-mono"
                  style={{
                    background: 'var(--panel)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '2px 6px',
                  }}
                >
                  {e.method} {e.path}
                </span>
                {' '}
                <span className="api-scope">scope: {e.scope}</span>
              </p>
              <p>{rich(e.summary)}</p>

              {e.params.length ? (
                <Table
                  expandable={false}
                  wrap
                  tableClass="api-table"
                  style={{ margin: '0 0 10px', minWidth: 700 }}
                  columns={[{ label: 'Parameter', width: 140 }, { label: 'Meaning' }]}
                  rows={e.params.map((pr, j) => ({
                    key: `${e.path}-${j}`,
                    cells: [
                      // The page's cell: the code span, then the required marker's span — the space between them
                      // is a text node of the cell, not part of either span.
                      <Fragment key="p">
                        <span className="api-mono">{pr.name}</span>
                        {pr.required ? (
                          <Fragment>
                            {' '}
                            <span style={{ color: 'var(--red)' }} title="required">*</span>
                          </Fragment>
                        ) : null}
                      </Fragment>,
                      <div key="m" style={{ color: 'var(--muted)', maxWidth: 760, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                        {rich(pr.description)}
                      </div>,
                    ],
                  }))}
                />
              ) : null}

              {e.shapes ? (
                <>
                  <p style={{ marginBottom: 6 }}>
                    <strong>Three shapes, chosen by the request</strong>
                  </p>
                  <ul>
                    {e.shapes.map((sh) => (
                      <li key={sh.when}>
                        <span className="api-mono" style={{ color: 'var(--text)' }}>
                          {sh.when}
                        </span>{' '}
                        → {rich(sh.shape)}
                        {sh.mode ? (
                          <>
                            {' ('}
                            <span className="api-mono">{`mode: "${sh.mode}"`}</span>
                            {')'}
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              {e.fields ? (
                <>
                  <p style={{ marginBottom: 6 }}>
                    <strong>Field vocabulary</strong> (<span className="api-mono">fields=</span>)
                  </p>
                  <p style={{ marginBottom: 10 }}>
                    {e.fields.map((f, i) => {
                      const isRange = (e.rangeFields || []).includes(f);
                      // The chips are separated by a space in views/api-reference.ejs (`</span> <span`), which
                      // is what gives each one its 4px gap; with none the row ran together and every chip
                      // after the first sat progressively further left than its twin.
                      return (
                        <Fragment key={f}>
                          {i ? ' ' : ''}
                          <span className={isRange ? 'api-chip range' : 'api-chip'}>
                            {f}
                            {isRange ? (
                              <span className="api-chip-tag" title="only the per-ticker source has it">
                                range
                              </span>
                            ) : null}
                          </span>
                        </Fragment>
                      );
                    })}
                  </p>
                  <p className="api-note">
                    The chips marked <span className="api-chip-tag">range</span> come only from the per-ticker
                    source; naming one in a projection therefore takes the slower path.
                  </p>
                </>
              ) : null}

              <p style={{ marginBottom: 6 }}>
                <strong>Example</strong>
              </p>
              <Code id={`route-${slug}-example`} copyId={`route-${slug}-example-copy`} gap={10} text={withBase(e.example, setup.base)} />
              <p style={{ marginBottom: 6 }}>
                <strong>Returns</strong>
              </p>
              <Code id={`route-${slug}-returns`} copyId={`route-${slug}-returns-copy`} gap={10} text={withBase(e.returns, setup.base)} />

              {e.errors ? (
                <>
                  <p style={{ marginBottom: 6 }}>
                    <strong>Errors</strong> — every one is{' '}
                    <span className="api-mono">{'{"ok":false,"error":"…"}'}</span>
                  </p>
                  <ul>
                    {e.errors.map((x, i) => (
                      <li key={i}>{rich(x)}</li>
                    ))}
                  </ul>
                </>
              ) : null}

              {e.notes ? (
                <ul style={{ marginBottom: 0 }}>
                  {e.notes.map((n, i) => (
                    <li key={i}>{rich(n)}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="panel-card" id="api-session-only">
        <h2 style={{ margin: '0 0 6px' }}>Session-only routes</h2>
        <p className="api-note" style={{ margin: 0 }}>
          These routes exist but need a browser session rather than a token, so no token can reach them:{' '}
          {docs.SESSION_ONLY.map((so, i) => (
            <Fragment key={so.path}>
              <span className="api-mono" style={{ color: 'var(--text)' }}>
                {so.path}
              </span>{' '}
              ({so.note}){i === docs.SESSION_ONLY.length - 1 ? '.' : ', '}
            </Fragment>
          ))}
        </p>
      </div>
    </>
  );
}